// Surface geometry for Smart Object placement.
//
// Ported from the Mockee PSD proof of concept (src/geometry.js). The algorithms
// are unchanged; this version adds an output `scale` (so a template can be baked
// at preview resolution) and a coarse forward grid used by the on-mockup
// placement overlay. Supports perspective transforms, custom/split warps and
// Photoshop's Cylinder, Arc and Arch warps.
//
// Coordinate conventions:
//   (u, v) in [0, 1]² is the flat design space of the Smart Object content,
//   top-left origin. (x, y) are document pixels (times `scale`).

export function homography(corners) {
  const [x0, y0, x1, y1, x2, y2, x3, y3] = corners;
  const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
  const det = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(det) < 1e-10) throw new Error("Degenerate Smart Object transform.");
  const g = (dx3 * dy2 - dx2 * dy3) / det;
  const h = (dx1 * dy3 - dx3 * dy1) / det;
  return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h];
}

export function project(H, u, v) {
  const w = H[6] * u + H[7] * v + 1;
  return { x: (H[0] * u + H[1] * v + H[2]) / w, y: (H[3] * u + H[4] * v + H[5]) / w, w };
}

const bernstein = (t) => [(1 - t) ** 3, 3 * t * (1 - t) ** 2, 3 * t * t * (1 - t), t ** 3];

export function patch(points, u, v) {
  const U = bernstein(u), V = bernstein(v);
  let x = 0, y = 0;
  for (let j = 0; j < 4; j++) {
    for (let i = 0; i < 4; i++) {
      const p = points[j * 4 + i], b = U[i] * V[j];
      x += p.x * b;
      y += p.y * b;
    }
  }
  return { x, y };
}

// Photoshop split warps are a row-major lattice of adjoining cubic patches.
// Every extra patch adds three controls; neighboring patches share their edge.
export function warpLayout(p) {
  const w = p.warp, e = w?.customEnvelopeWarp;
  if (!e?.meshPoints?.length) throw new Error("The custom warp has no control points.");
  const cols = w.deformNumCols ?? 4, rows = w.deformNumRows ?? 4;
  if (!Number.isInteger(cols) || !Number.isInteger(rows) || cols < 4 || rows < 4 || (cols - 1) % 3 || (rows - 1) % 3 || cols * rows !== e.meshPoints.length) {
    throw new Error("Unsupported warp lattice: control dimensions must be 3n + 1 and match the stored points.");
  }
  if (!e.meshPoints.every((q) => Number.isFinite(q.x) && Number.isFinite(q.y))) throw new Error("Invalid warp control coordinates.");
  const value = (v, d) => v?.value ?? d, b = w.bounds;
  const left = value(b?.left, 0), top = value(b?.top, 0);
  const width = value(b?.right, p.width) - left, height = value(b?.bottom, p.height) - top;
  if (!(width > 0 && height > 0)) throw new Error("Invalid Smart Object bounds.");

  function knots(raw, count, start, size) {
    if (count === 1) return [0, 1];
    if (!raw?.length) throw new Error("Split warp is missing its Photoshop slice positions.");
    // Full slice lists carry their own source-coordinate extent. Photoshop
    // may offset this extent relative to warp.bounds (including edge padding).
    const origin = raw.length === count + 1 ? raw[0] : start;
    const extent = raw.length === count + 1 ? raw.at(-1) - origin : size;
    if (!(extent > 0)) throw new Error("Invalid Photoshop warp slice positions.");
    let values = raw.map((x) => (x - origin) / extent);
    if (values.length === count - 1) values = [0, ...values, 1];
    if (values.length !== count + 1 || Math.abs(values[0]) > 1e-5 || Math.abs(values.at(-1) - 1) > 1e-5 || values.some((x, i) => !Number.isFinite(x) || (i && x <= values[i - 1]))) {
      throw new Error("Invalid Photoshop warp slice positions.");
    }
    values[0] = 0;
    values[values.length - 1] = 1;
    return values;
  }

  return {
    cols, rows, left, top, width, height,
    x: knots(e.quiltSliceX, (cols - 1) / 3, left, width),
    y: knots(e.quiltSliceY, (rows - 1) / 3, top, height),
    points: e.meshPoints,
  };
}

// Cylinder reconstruction adapted from Strayta (MIT, see THIRD-PARTY-NOTICES.md),
// via the Mockee PSD proof of concept. Photoshop stores a Cylinder warp as a
// front rectangle plus top/bottom ellipse ratios; these become four adjoining
// cubic patches (a 13 × 4 lattice). The narrow outside patches collapse at the
// edges, so the design wraps around the sides.
export function cylinderLayout(p) {
  const warp = p.warp, values = warp?.values;
  if (!Array.isArray(values) || values.length !== 7 || !values.every(Number.isFinite)) throw new Error("Cylinder warp requires seven finite parameters.");
  if (warp.rotate && warp.rotate !== "horizontal") throw new Error("Vertical Cylinder warps are not supported yet.");
  if ((warp.perspective ?? 0) !== 0 || (warp.perspectiveOther ?? 0) !== 0) throw new Error("Cylinder warp perspective parameters are not supported yet.");
  const [leftFront, bottomFront, rightFront, topFront, topRatio, bottomRatio] = values;
  if (!(rightFront > leftFront && bottomFront > topFront)) throw new Error("Invalid Cylinder front rectangle.");
  const value = (v, d) => v?.value ?? d, b = warp.bounds;
  const left = value(b?.left, 0), top = value(b?.top, 0);
  const width = value(b?.right, p.width) - left, height = value(b?.bottom, p.height) - top;
  if (!(width > 0 && height > 0)) throw new Error("Invalid Cylinder source bounds.");
  const radius = (rightFront - leftFront) / 2, center = (leftFront + rightFront) / 2;
  const xs = [leftFront, leftFront, leftFront, leftFront, leftFront, center - 0.54045 * radius, center, center + 0.54045 * radius, rightFront, rightFront, rightFront, rightFront, rightFront];
  const sag = [0, 0, 0, 0, 0.5274, 1.01455, 1, 1.01455, 0.5274, 0, 0, 0, 0], points = [];
  for (let row = 0; row < 4; row++) {
    const t = row / 3, y = topFront + (bottomFront - topFront) * t, depth = radius * (topRatio + (bottomRatio - topRatio) * t);
    for (let col = 0; col < 13; col++) points.push({ x: xs[col], y: y + depth * sag[col] });
  }
  const sliver = 0.0062421;
  return {
    cols: 13, rows: 4, left, top, width, height, points,
    x: [-0.6 / width, sliver, 0.5, 1 - sliver, 1 + 0.6 / width],
    y: [0, 1],
    // Design-space columns sampled by the mesh (the patch knots extend past the edges).
    sampleX: [0, sliver, 0.5, 1 - sliver, 1],
  };
}

// Bend and source box of a horizontal Arc/Arch preset (the only validated form).
function presetBox(p, name) {
  const warp = p.warp, bend = warp?.value ?? 0;
  if (!Number.isFinite(bend)) throw new Error(`${name} warp requires a finite bend.`);
  if (warp.rotate && warp.rotate !== "horizontal") throw new Error(`Vertical ${name} warps are not supported yet.`);
  if ((warp.perspective ?? 0) !== 0 || (warp.perspectiveOther ?? 0) !== 0) throw new Error(`${name} warp distortion parameters are not supported yet.`);
  const value = (v, d) => v?.value ?? d, b = warp.bounds;
  const left = value(b?.left, 0), top = value(b?.top, 0);
  const width = value(b?.right, p.width) - left, height = value(b?.bottom, p.height) - top;
  if (!(width > 0 && height > 0)) throw new Error(`Invalid ${name} source bounds.`);
  return { bend, left, top, width, height, theta: Math.PI * Math.min(1, Math.abs(bend) / 100) };
}

/** Undeformed 4 × 4 lattice of a box (zero bend). */
function flatLattice({ left, top, width, height }) {
  const points = [];
  for (let row = 0; row < 4; row++) for (let col = 0; col < 4; col++) points.push({ x: left + (width * col) / 3, y: top + (height * row) / 3 });
  return points;
}

// Photoshop's Arc and Arch presets are one cubic patch (a 4 × 4 lattice)
// whose rows are circular arcs spanning π·|bend|, in pixels, each drawn with
// the standard circular-arc handles. Measured against Photoshop's rendered
// layers (Arc: bends from -35% to +11%; Arch: -7.6%).
//
// Arc: the rows are concentric. The edge nearest the centre keeps its corners
// (its chord stays the box width) and the other rows lie radially outside it,
// one box height apart. The centre is above the box for a negative bend and
// below it for a positive one.
export function arcLayout(p) {
  const box = presetBox(p, "Arc"), { bend, left, top, width, height, theta } = box;
  if (theta < 1e-6) return { cols: 4, rows: 4, left, top, width, height, points: flatLattice(box), x: [0, 1], y: [0, 1] };
  const half = theta / 2, inner = width / 2 / Math.sin(half), k = (4 / 3) * Math.tan(theta / 4), points = [];
  for (let row = 0; row < 4; row++) {
    // Rows from the inner edge outwards, measured from the inner edge's chord.
    const r = inner + (height * row) / 3, depth = inner * Math.cos(half);
    const at = (a) => ({ x: width / 2 + r * Math.sin(a), d: r * Math.cos(a) - depth });
    const p0 = at(-half), p3 = at(half), tx = r * Math.cos(half), td = r * Math.sin(half);
    const ring = [p0, { x: p0.x + k * tx, d: p0.d + k * td }, { x: p3.x - k * tx, d: p3.d + k * td }, p3];
    // Negative bend: the inner edge is the top; positive: the bottom (rows reversed).
    const y = (d) => (bend < 0 ? top + d : top + height - d);
    points.push(ring.map((q) => ({ x: left + q.x, y: y(q.d) })));
  }
  if (bend > 0) points.reverse();
  return { cols: 4, rows: 4, left, top, width, height, points: points.flat(), x: [0, 1], y: [0, 1] };
}

// Arch: every row is the same arc through the box's left and right edges at
// that row's height, so the sides stay straight and vertical. A positive bend
// raises the middle, a negative one lowers it.
export function archLayout(p) {
  const box = presetBox(p, "Arch"), { bend, left, top, width, height, theta } = box;
  if (theta < 1e-6) return { cols: 4, rows: 4, left, top, width, height, points: flatLattice(box), x: [0, 1], y: [0, 1] };
  const half = theta / 2, r = width / 2 / Math.sin(half), k = (4 / 3) * Math.tan(theta / 4), sign = bend > 0 ? -1 : 1;
  const tx = r * Math.cos(half), ty = sign * r * Math.sin(half), points = [];
  for (let row = 0; row < 4; row++) {
    const y = top + (height * row) / 3;
    points.push({ x: left, y }, { x: left + k * tx, y: y + k * ty }, { x: left + width - k * tx, y: y + k * ty }, { x: left + width, y });
  }
  return { cols: 4, rows: 4, left, top, width, height, points, x: [0, 1], y: [0, 1] };
}

/** Bézier lattice of a warped placement, or null for a plain (perspective) transform. */
export function surfaceLayout(p) {
  const style = p?.warp?.style;
  if (style === "cylinder") return cylinderLayout(p);
  if (style === "arc") return arcLayout(p);
  if (style === "arch") return archLayout(p);
  if (style === "custom") return warpLayout(p);
  return null;
}

const SUPPORTED_WARPS = ["none", "custom", "cylinder", "arc", "arch"];

/** Returns null when the placement can be baked natively, or a reason string. */
export function supported(placed) {
  if (!placed) return "Select a Smart Object.";
  const w = placed.warp;
  if (w?.style && !SUPPORTED_WARPS.includes(w.style)) return `Built-in warp “${w.style}” is not supported yet.`;
  try {
    surfaceLayout(placed);
  } catch (e) {
    return e.message;
  }
  return null;
}

export function quiltPoint(layout, points, u, v) {
  function locate(knots, t) {
    let i = 0;
    while (i < knots.length - 2 && t >= knots[i + 1]) i++;
    return [i, (t - knots[i]) / (knots[i + 1] - knots[i])];
  }
  const [col, a] = locate(layout.x, u), [row, b] = locate(layout.y, v), controls = [];
  for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++) controls.push(points[(row * 3 + j) * layout.cols + col * 3 + i]);
  return patch(controls, a, b);
}

/**
 * Builds the forward mapping (u, v) -> document (x, y) for a placed layer.
 * Undeformed grids use the exact projective plane, preserving straight lines.
 */
export function surfaceMapping(p) {
  const error = supported(p);
  if (error) throw new Error(error);
  const H = homography(p.nonAffineTransform || p.transform);
  const layout = surfaceLayout(p), points = layout?.points;
  let projected = null;
  if (points) {
    const control = (knots, i) => {
      const segment = Math.min(Math.floor(i / 3), knots.length - 2);
      return knots[segment] + ((knots[segment + 1] - knots[segment]) * (i - segment * 3)) / 3;
    };
    const undeformed = points.every((q, i) =>
      Math.abs(q.x - (layout.left + control(layout.x, i % layout.cols) * layout.width)) < 1e-5 &&
      Math.abs(q.y - (layout.top + control(layout.y, Math.floor(i / layout.cols)) * layout.height)) < 1e-5,
    );
    const xs = points.map((q) => q.x), ys = points.map((q) => q.y);
    const minX = Math.min(...xs), minY = Math.min(...ys), width = Math.max(...xs) - minX, height = Math.max(...ys) - minY;
    if (!(width > 0 && height > 0)) throw new Error("Degenerate warp surface.");
    if (!undeformed) projected = points.map((q) => project(H, (q.x - minX) / width, (q.y - minY) / height));
  }
  const map = (u, v) => (projected ? { ...quiltPoint(layout, projected, u, v), w: 1 } : project(H, u, v));
  return { layout, map };
}

export function createVertices(p, steps = 96, scale = 1) {
  const { layout, map } = surfaceMapping(p);
  // Include slice boundaries exactly, including uneven Photoshop splits.
  const sample = (knots, i) => {
    if (i === steps) return 1;
    const t = (i / steps) * (knots.length - 1), k = Math.floor(t);
    return knots[k] + (knots[k + 1] - knots[k]) * (t - k);
  };
  const out = [];
  for (let j = 0; j <= steps; j++) {
    for (let i = 0; i <= steps; i++) {
      const u = layout ? sample(layout.sampleX || layout.x, i) : i / steps, v = layout ? sample(layout.y, j) : j / steps;
      const q = map(u, v);
      out.push({ x: q.x * scale, y: q.y * scale, w: q.w, u, v });
    }
  }
  return out;
}

export function rasterTriangle(a, b, c, width, height, uv, coverage) {
  const area = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  if (Math.abs(area) < 1e-9) return;
  const x0 = Math.max(0, Math.floor(Math.min(a.x, b.x, c.x))), x1 = Math.min(width - 1, Math.ceil(Math.max(a.x, b.x, c.x)));
  const y0 = Math.max(0, Math.floor(Math.min(a.y, b.y, c.y))), y1 = Math.min(height - 1, Math.ceil(Math.max(a.y, b.y, c.y)));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const px = x + 0.5, py = y + 0.5;
      const B = ((px - a.x) * (c.y - a.y) - (py - a.y) * (c.x - a.x)) / area;
      const C = ((b.x - a.x) * (py - a.y) - (b.y - a.y) * (px - a.x)) / area;
      const A = 1 - B - C;
      if (A < -1e-7 || B < -1e-7 || C < -1e-7) continue;
      const den = A / a.w + B / b.w + C / c.w;
      const k = y * width + x;
      uv[k * 2] = (A * a.u / a.w + B * b.u / b.w + C * c.u / c.w) / den;
      uv[k * 2 + 1] = (A * a.v / a.w + B * b.v / b.w + C * c.v / c.w) / den;
      coverage[k] = 255;
    }
  }
}

/**
 * Rasterizes an inverse UV lookup: every covered output pixel stores the flat
 * design coordinate it shows. `scale` maps document pixels to output pixels.
 */
export async function bakeMap(p, width, height, progress = () => {}, steps = 96, scale = 1) {
  const layout = surfaceLayout(p);
  if (layout) {
    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const x = layout.x.length - 1, y = layout.y.length - 1, multiple = (x * y) / gcd(x, y);
    steps = Math.ceil(Math.max(steps, 12 * x, 12 * y) / multiple) * multiple;
    if (steps > 768) throw new Error("This warp needs too many subdivisions.");
  }
  const vertices = createVertices(p, steps, scale);
  const uv = new Float32Array(width * height * 2), coverage = new Uint8Array(width * height);
  for (let j = 0; j < steps; j++) {
    for (let i = 0; i < steps; i++) {
      const a = vertices[j * (steps + 1) + i], b = vertices[j * (steps + 1) + i + 1];
      const c = vertices[(j + 1) * (steps + 1) + i + 1], d = vertices[(j + 1) * (steps + 1) + i];
      rasterTriangle(a, b, c, width, height, uv, coverage);
      rasterTriangle(a, c, d, width, height, uv, coverage);
    }
    if (j % 8 === 0) {
      progress(j / steps);
      await new Promise((r) => setTimeout(r, 0));
    }
  }
  progress(1);
  return { width, height, uv, coverage, steps };
}

/**
 * Coarse forward grid for UI overlays: (n + 1)² points, each the output-space
 * position of a uniform (u, v) sample, interleaved x, y.
 */
export function forwardGrid(p, n = 32, scale = 1) {
  const { map } = surfaceMapping(p);
  const out = new Float32Array((n + 1) * (n + 1) * 2);
  for (let j = 0; j <= n; j++) {
    for (let i = 0; i <= n; i++) {
      const q = map(i / n, j / n), k = (j * (n + 1) + i) * 2;
      out[k] = q.x * scale;
      out[k + 1] = q.y * scale;
    }
  }
  return out;
}

export function packUV(uv) {
  const out = new Uint8ClampedArray(uv.length * 2);
  for (let i = 0; i < uv.length / 2; i++) {
    const u = Math.round(Math.max(0, Math.min(1, uv[i * 2])) * 65535), v = Math.round(Math.max(0, Math.min(1, uv[i * 2 + 1])) * 65535);
    out[i * 4] = u >>> 8;
    out[i * 4 + 1] = u & 255;
    out[i * 4 + 2] = v >>> 8;
    out[i * 4 + 3] = v & 255;
  }
  return out;
}
